import dynamic from 'next/dynamic';
import type React from 'react';
import { PortalContextProvider } from '@/common/Portal/PortalContext';
import { PortalContextProvider as SearchPortalContextProvider } from '@/srp/Portal/PortalContext';
import {
  browserCookieIdExperiments,
  userIdExperiments
} from '~/constants/srp/experiments';
import { AlertsContextProvider } from '~/context/common/AlertsContext';
import {
  DeviceContextProvider,
  type OwnProps as DeviceContextProps
} from '~/context/common/DeviceContext';
import ExperimentProvider, {
  type PropsFromServer as ExperimentContextProps
} from '~/context/common/ExperimentProvider';
import {
  NativeContextProvider,
  type OwnProps as NativeContextProps
} from '~/context/common/NativeContext';
import {
  StaticContextProvider as CommonStaticContextProvider,
  type OwnProps as CommonStaticContextProps
} from '~/context/common/StaticContext';
import {
  TradeContextProvider as CommonTradeContextProvider,
  type OwnProps as CommonTradeContextProps
} from '~/context/common/TradeContext';
import {
  UserContextProvider,
  type OwnProps as UserContextProps
} from '~/context/common/UserContext';
import {
  AppliedFiltersProvider,
  type OwnProps as AppliedFiltersContextProps
} from '~/context/srp/AppliedFiltersContext';
import {
  ConditionalTileContextProvider,
  type OwnProps as ConditionalTileContextProps
} from '~/context/srp/ConditionalTileContext';
import { ErrorContextProvider } from '~/context/srp/ErrorContext';
import {
  FacetDataContextProvider,
  type OwnProps as FacetDataContextProps
} from '~/context/srp/FacetDataContext';
import {
  FinanceContextProvider,
  type FinanceContextProps
} from '~/context/srp/FinanceContext';
import {
  InventoryContextProvider,
  type OwnProps as InventoryContextProps
} from '~/context/srp/InventoryContext';
import { ModalContextProvider } from '~/context/srp/ModalContext';
import { PricingContextProvider } from '~/context/srp/PricingContext';
import {
  RouteContextProvider,
  type OwnProps as RouteContextProps
} from '~/context/srp/RouteContext';
import { SavedSearchesContextProvider } from '~/context/srp/SavedSearchesContext';
import { SearchAnalyticsContextProvider } from '~/context/srp/SearchAnalyticsContext';
import {
  SearchContextProvider,
  type OwnProps as SearchContextProps
} from '~/context/srp/SearchContext';
import {
  SearchEventsDataContextProvider,
  type OwnProps as SearchEventDataContextProps
} from '~/context/srp/SearchEventsDataContext';
import {
  SEOContextProvider,
  type OwnProps as SEOContextProps
} from '~/context/srp/SEOContext';
import {
  StaticContextProvider,
  type OwnProps as StaticContextProps
} from '~/context/srp/StaticContext';
import { SuggestionsContextProvider } from '~/context/srp/SuggestionsContext';
import AnalyticsProvider from './AnalyticsProvider';

const CoreAuth = dynamic(
  () => import(/* webpackChunkName: "CoreAuth" */ '@/common/CoreAuth'),
  { ssr: false }
);

export type OwnProps = React.PropsWithChildren<{
  forAppliedFiltersContext: AppliedFiltersContextProps;
  forCommonStaticContext: CommonStaticContextProps;
  forCommonTradeContext: CommonTradeContextProps;
  forConditionalTileContext: ConditionalTileContextProps;
  forDeviceContext: DeviceContextProps;
  forExperimentContext: ExperimentContextProps;
  forFacetDataContext: FacetDataContextProps;
  forFinanceContext: FinanceContextProps;
  forInventoryContext: InventoryContextProps;
  forNativeContext: NativeContextProps;
  forRouteContext: RouteContextProps;
  forSearchContext: SearchContextProps;
  forSearchEventDataContext: SearchEventDataContextProps;
  forSEOContext: SEOContextProps;
  forStaticContext: StaticContextProps;
  forUserContext: UserContextProps;
}>;

export default function SearchProviders({
  children,
  forAppliedFiltersContext,
  forCommonStaticContext,
  forCommonTradeContext,
  forConditionalTileContext,
  forDeviceContext,
  forExperimentContext,
  forFacetDataContext,
  forFinanceContext,
  forInventoryContext,
  forNativeContext,
  forRouteContext,
  forSearchContext,
  forSearchEventDataContext,
  forSEOContext,
  forStaticContext,
  forUserContext
}: OwnProps) {
  return (
    <CommonStaticContextProvider {...forCommonStaticContext}>
      <StaticContextProvider {...forStaticContext}>
        <NativeContextProvider {...forNativeContext}>
          <ExperimentProvider
            {...forExperimentContext}
            browserCookieIdExperiments={browserCookieIdExperiments}
            userIdExperiments={userIdExperiments}
          >
            <AnalyticsProvider project="search" track="search">
              <DeviceContextProvider {...forDeviceContext}>
                <UserContextProvider {...forUserContext}>
                  <SearchContextProvider {...forSearchContext}>
                    <PricingContextProvider>
                      <AlertsContextProvider>
                        <RouteContextProvider {...forRouteContext}>
                          <SEOContextProvider {...forSEOContext}>
                            <ConditionalTileContextProvider
                              {...forConditionalTileContext}
                            >
                              <FacetDataContextProvider
                                {...forFacetDataContext}
                              >
                                <ErrorContextProvider>
                                  <InventoryContextProvider
                                    {...forInventoryContext}
                                  >
                                    <SearchAnalyticsContextProvider>
                                      <FinanceContextProvider
                                        {...forFinanceContext}
                                      >
                                        <CommonTradeContextProvider
                                          {...forCommonTradeContext}
                                        >
                                          <AppliedFiltersProvider
                                            {...forAppliedFiltersContext}
                                          >
                                            <SearchEventsDataContextProvider
                                              {...forSearchEventDataContext}
                                            >
                                              <SuggestionsContextProvider>
                                                <PortalContextProvider>
                                                  <SearchPortalContextProvider>
                                                    <ModalContextProvider>
                                                      <SavedSearchesContextProvider>
                                                        <CoreAuth />
                                                        {children}
                                                      </SavedSearchesContextProvider>
                                                    </ModalContextProvider>
                                                  </SearchPortalContextProvider>
                                                </PortalContextProvider>
                                              </SuggestionsContextProvider>
                                            </SearchEventsDataContextProvider>
                                          </AppliedFiltersProvider>
                                        </CommonTradeContextProvider>
                                      </FinanceContextProvider>
                                    </SearchAnalyticsContextProvider>
                                  </InventoryContextProvider>
                                </ErrorContextProvider>
                              </FacetDataContextProvider>
                            </ConditionalTileContextProvider>
                          </SEOContextProvider>
                        </RouteContextProvider>
                      </AlertsContextProvider>
                    </PricingContextProvider>
                  </SearchContextProvider>
                </UserContextProvider>
              </DeviceContextProvider>
            </AnalyticsProvider>
          </ExperimentProvider>
        </NativeContextProvider>
      </StaticContextProvider>
    </CommonStaticContextProvider>
  );
}
